/* eslint-disable no-case-declarations */
import { useReducer } from 'react'

import type {
  ProductColumnMatchInfo,
  SelectedColumn,
} from '@Store/types/product'

import aptProps from '../constants/aptProps'
import uptcProps from '../constants/uptcProps'
import uptProps from '../constants/uptProps'
import type ColumnMappingProps from '../types/ColumnMappingProps'

export type MatchFieldsState = {
  mode?: 'apt' | 'upt' | 'upt-c' | 'feed'
  matchData?: ProductColumnMatchInfo[]
  options?: ColumnMappingProps[]
  matchedProperties?: ProductColumnMatchInfo[]
  unmatchedProperties?: ProductColumnMatchInfo[]
  matchedSelected?: SelectedColumn[]
  unmatchedSelected?: SelectedColumn[]
  allSelected?: SelectedColumn[]
  requiredNotSelected?: ColumnMappingProps[]
}

export type MatchFieldsActions = {
  setMode: (mode: MatchFieldsState['mode']) => void
  setOptions: (options: ColumnMappingProps[]) => void
  setMatchData: (
    data: ProductColumnMatchInfo[],
    matchWith?: keyof ProductColumnMatchInfo | string,
    checkWith?: keyof ColumnMappingProps,
  ) => void
  setMatchedSelected: (data: SelectedColumn[]) => void
  removeMatchedSelected: (index: number) => void
  setUnmatchedSelected: (data: SelectedColumn[]) => void
  removeUnmatchedSelected: (index: number) => void
  clearState: () => void
}

export function matchReducer(
  state: Partial<MatchFieldsState>,
  action: { type: string; payload: any },
): Partial<MatchFieldsState> {
  switch (action.type) {
    case 'SET_MODE':
      const newMode = action.payload
      const newOptions =
        newMode === 'apt'
          ? aptProps
          : newMode === 'upt'
            ? uptProps
            : newMode === 'upt-c'
              ? uptcProps
              : ([] as ColumnMappingProps[])

      return {
        ...state,
        mode: newMode,
        options: newOptions,
      }

    case 'SET_OPTIONS':
      return {
        ...state,
        options: action.payload,
      }

    case 'SET_MATCH_DATA':
      const { matchData, matchWith, checkWith } = action.payload ?? {
        matchData: [],
        matchWith: 'name',
        checkWith: 'text',
      }
      const matchKey = matchWith as keyof ProductColumnMatchInfo
      const checkKey = checkWith as keyof ColumnMappingProps

      const newMatchedProperties = [...matchData]?.reduce(
        (acc: ProductColumnMatchInfo[], cur: ProductColumnMatchInfo) => {
          const matchedProp = state.options?.find(
            (item) =>
              item[checkKey] === cur[matchKey] &&
              !acc.find(
                (i) =>
                  (i as Record<string, unknown>)[checkKey as string] ===
                  cur[matchKey],
              ),
          )
          if (matchedProp) {
            acc.push({
              ...matchedProp,
              ...cur,
            })
          }
          return acc
        },
        [],
      )

      const newUnmatchedProperties = [...matchData]?.reduce(
        (acc: ProductColumnMatchInfo[], cur: ProductColumnMatchInfo) => {
          const matchedProps = newMatchedProperties?.find(
            (item: ProductColumnMatchInfo) => item.index === cur.index,
          )

          if (!matchedProps) {
            acc.push({
              ...cur,
              key:
                state?.mode === 'upt-c' &&
                cur[matchKey].toString().startsWith('Competitor')
                  ? 'otherurl'
                  : cur.key,
            })
          }
          return acc
        },
        [],
      )

      const newMatchedSelected = newMatchedProperties?.map(
        (item: ProductColumnMatchInfo) => ({
          index: item.index,
          key: item.key,
          isSelected: true,
          ...('id' in item ? { id: item.id } : {}),
          ...('value' in item ? { initialValue: item.value } : {}),
          ...('name' in item ? { name: item.name } : {}),
        }),
      )

      return {
        ...state,
        matchedProperties: newMatchedProperties,
        matchedSelected: newMatchedSelected,
        unmatchedProperties: newUnmatchedProperties,
        unmatchedSelected: [],
      }

    case 'SET_MATCHED_SELECTED':
      return { ...state, matchedSelected: [...action.payload] }

    case 'REMOVE_MATCHED_SELECTED':
      return {
        ...state,
        matchedSelected: state.matchedSelected?.filter(
          (i) => i.index !== action.payload,
        ),
      }

    case 'SET_UNMATCHED_SELECTED':
      return { ...state, unmatchedSelected: [...action.payload] }

    case 'REMOVE_UNMATCHED_SELECTED':
      return {
        ...state,
        unmatchedSelected: state.unmatchedSelected?.filter(
          (i) => i.index !== action.payload,
        ),
      }

    case 'CLEAR_STATE':
      return {}

    default:
      return state
  }
}

function useMatchFieldsReducer(): {
  state: MatchFieldsState
  actions: MatchFieldsActions
} {
  const [
    {
      mode,
      options,
      matchedProperties,
      unmatchedProperties,
      matchedSelected = [],
      unmatchedSelected = [],
    },
    matchDispatch,
  ] = useReducer(matchReducer, {}, (): MatchFieldsState => ({}))

  const allSelected = [...matchedSelected, ...unmatchedSelected]
  const requiredNotSelected = options?.filter(
    (i) => i.required && !allSelected.find((j) => j.key === i.key),
  )

  const setMode = (data: MatchFieldsState['mode']): void => {
    matchDispatch({ type: 'SET_MODE', payload: data })
  }

  const setOptions = (data: ColumnMappingProps[]): void => {
    matchDispatch({ type: 'SET_OPTIONS', payload: data })
  }

  const setMatchData = (
    data: ProductColumnMatchInfo[],
    matchWith: keyof ProductColumnMatchInfo | string = 'name',
    checkWith: keyof ColumnMappingProps = 'text',
  ): void => {
    matchDispatch({
      type: 'SET_MATCH_DATA',
      payload: { matchData: data, matchWith, checkWith },
    })
  }

  const setMatchedSelected = (data: SelectedColumn[]): void => {
    matchDispatch({ type: 'SET_MATCHED_SELECTED', payload: data })
  }

  const removeMatchedSelected = (index: number): void => {
    matchDispatch({ type: 'REMOVE_MATCHED_SELECTED', payload: index })
  }

  const setUnmatchedSelected = (data: SelectedColumn[]): void => {
    matchDispatch({ type: 'SET_UNMATCHED_SELECTED', payload: data })
  }

  const removeUnmatchedSelected = (index: number): void => {
    matchDispatch({ type: 'REMOVE_UNMATCHED_SELECTED', payload: index })
  }

  const clearState = (): void => {
    matchDispatch({ type: 'CLEAR_STATE', payload: null })
  }

  return {
    state: {
      mode,
      options,
      matchedProperties,
      unmatchedProperties,
      matchedSelected,
      unmatchedSelected,
      allSelected,
      requiredNotSelected,
    },
    actions: {
      setMode,
      setOptions,
      setMatchData,
      setMatchedSelected,
      removeMatchedSelected,
      setUnmatchedSelected,
      removeUnmatchedSelected,
      clearState,
    },
  }
}

export default useMatchFieldsReducer
